import { useState } from 'react'
import Select from 'react-select'
import { colourStyles } from './selectStyle'
import { SelectOptionType } from '@/app/types/constantDataType'
/**
 * Props for the MultiSelect component.
 */
interface MultiSelectType {
  id: string
  placeholder: string
  value: SelectOptionType[]
  options: SelectOptionType[]
  setValue: (value: SelectOptionType[] | null) => void
  isLoading?: boolean
  isDisabled?: boolean
  inputValue?: string
  setInputValue?: (value: string) => void
  multiSelectComponents?: any
  error?: boolean
  selectAllOptionEnabled?: boolean
  noOptionsMessage?: string
  hideSelectedOptions?: boolean
}

/**
 * MultiSelect Component
 *
 * A customizable, multi-select dropdown component built on `react-select`.
 * Supports selecting multiple options, custom components, and additional features like "Select All".
 *
 * @component
 * @example
 * // Example Usage:
 * const options = [
 *   { id: 1, label: "Option 1", value: "option1" },
 *   { id: 2, label: "Option 2", value: "option2" },
 *   { id: 3, label: "Option 3", value: "option3" },
 * ];
 *
 * const [selectedOptions, setSelectedOptions] = useState<SelectOptionType[]>([]);
 *
 * <MultiSelect
 *   id="example-multi-select"
 *   placeholder="Select options"
 *   value={selectedOptions}
 *   options={options}
 *   setValue={setSelectedOptions}
 *   isLoading={false}
 *   isDisabled={false}
 *   noOptionsMessage="No options found"
 *   selectAllOptionEnabled={true}
 * />;
 *
 * @param {string} id - Unique identifier for the select element.
 * @param {string} placeholder - Placeholder text for the dropdown.
 * @param {SelectOptionType[]} value - The currently selected options.
 * @param {SelectOptionType[]} options - List of available options to select from.
 * @param {Function} setValue - Callback function to handle selection changes.
 * @param {boolean} [isLoading=false] - Indicates if the dropdown is loading options.
 * @param {boolean} [isDisabled=false] - Indicates if the dropdown is disabled.
 * @param {string} [inputValue] - Controlled input value for search.
 * @param {Function} [setInputValue] - Callback to update the search input value.
 * @param {object} [multiSelectComponents] - Custom components to override the default react-select components.
 * @param {boolean} [error=false] - Indicates if the dropdown should display an error state.
 * @param {boolean} [selectAllOptionEnabled=true] - Enables or disables the "Select All" option.
 * @param {string} [noOptionsMessage="No options"] - Custom message displayed when no options are available.
 * @param {boolean} [hideSelectedOptions=true] - Determines whether selected options are hidden in the dropdown list.
 *
 * @returns {JSX.Element} The rendered MultiSelect component.
 */
const MultiSelect = ({
  id,
  placeholder,
  value,
  options,
  setValue,
  isLoading = false,
  isDisabled = false,
  multiSelectComponents,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  error = false,
  inputValue,
  setInputValue,
  noOptionsMessage,
  hideSelectedOptions = true,
  selectAllOptionEnabled = true,
}: MultiSelectType) => {
  const selectAllOption = {
    id: 0,
    label: value?.length === options?.length ? 'UnSelect All' : 'Select All',
    value: 'Select All',
    name: 'Select All',
    other: {
      allSelectSelected: value?.length === options?.length,
    },
  }

  const passByOptions = selectAllOptionEnabled
    ? [selectAllOption, ...options]
    : options

  const [localSearchText, setLocalSearchText] = useState<string>('')
  const handleInputChange = (e: string) => {
    if (setInputValue) {
      setInputValue(e)
    } else {
      setLocalSearchText(e)
    }
  }

  const handleValueChange = (selectedOption: SelectOptionType[] | null) => {
    if (!selectAllOptionEnabled) {
      setValue(selectedOption)
      return
    }

    const isSelectAllSelected = selectedOption?.some(
      (item) => item.id === selectAllOption.id
    )

    if (isSelectAllSelected) {
      const isPartialSelection =
        (selectedOption?.length ?? 0) - 1 < options?.length
      setValue(isPartialSelection ? options : [])
    } else {
      setValue(selectedOption)
    }
  }

  return (
    <Select
      className="w-full"
      id={id}
      inputValue={inputValue || localSearchText}
      onInputChange={handleInputChange}
      isLoading={isLoading}
      isDisabled={isDisabled}
      placeholder={placeholder}
      value={value}
      hideSelectedOptions={hideSelectedOptions}
      components={multiSelectComponents}
      options={passByOptions}
      noOptionsMessage={() => noOptionsMessage || 'No Options'}
      styles={colourStyles}
      onChange={(value) =>
        handleValueChange(value as SelectOptionType[] | null)
      }
      closeMenuOnSelect={false}
      isMulti
    />
  )
}

export default MultiSelect
